'use client';

import * as React from 'react';

import { Cross2Icon } from '@radix-ui/react-icons';
import { type VariantProps, cva } from 'class-variance-authority';
import { Dialog as SheetPrimitive } from 'radix-ui';

import { cn } from '../lib/utils';

const Sheet = SheetPrimitive.Root;

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

/**
 * A LIGHT scrim — deliberately much lighter than the dialog's.
 *
 * The two surfaces are asking for different things. A modal demands a decision,
 * so `bg-black/80` blacking out the page is the point. A side panel's whole
 * argument is that the list behind it stays available for reference — and at 80%
 * it does not: the page goes near-black and the panel reads as a takeover that
 * happens to be docked. Dimming just enough to push the page back, while leaving
 * it legible, is what makes a 45%-wide panel feel like a panel.
 *
 * Heavier in dark mode because a 20% black veil over an already-dark canvas is
 * invisible, so the layer separation would be lost.
 */
const SheetOverlay: React.FC<
  React.ComponentPropsWithRef<typeof SheetPrimitive.Overlay>
> = ({ className, ...props }) => (
  <SheetPrimitive.Overlay
    className={cn(
      "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=open]:duration-200 data-[state=closed]:duration-150 motion-reduce:animate-none fixed inset-0 z-50 bg-black/20 dark:bg-black/50",
      className,
    )}
    {...props}
  />
);
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

/**
 * Timing: 200ms in, 150ms out — not the inherited 500/300.
 *
 * Half a second is long enough to *watch*, which is what made every drawer feel
 * like a commitment rather than a glance; it also means clicking block-to-block
 * on the calendar spent more time animating than reading. ~200ms is the band
 * where a transition still reads as motion (so the panel's origin is legible)
 * without being something you wait for, and exits are quicker than entrances
 * because nobody needs to be shown where a thing went.
 *
 * `ease-out` on the way in — fast start, gentle settle — is what reads as
 * "light". The old `ease-in-out` accelerates from a standstill, which is the
 * curve that feels sluggish at any duration.
 */
const sheetVariants = cva(
  "bg-background data-[state=open]:animate-in data-[state=closed]:animate-out fixed z-50 gap-4 p-6 shadow-elevation-lg transition data-[state=open]:duration-200 data-[state=open]:ease-out data-[state=closed]:duration-150 data-[state=closed]:ease-in motion-reduce:transition-none motion-reduce:animate-none",
  {
    variants: {
      side: {
        top: "data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top inset-x-0 top-0 border-b",
        bottom:
          "data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom inset-x-0 bottom-0 border-t",
        left: "data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left inset-y-0 start-0 h-full w-3/4 border-e sm:max-w-sm ltr:pl-[var(--safe-area-inset-left,env(safe-area-inset-left,0px))] rtl:pr-[var(--safe-area-inset-right,env(safe-area-inset-right,0px))]",
        right:
          "data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 end-0 h-full w-3/4 border-s sm:max-w-sm ltr:pr-[var(--safe-area-inset-right,env(safe-area-inset-right,0px))] rtl:pl-[var(--safe-area-inset-left,env(safe-area-inset-left,0px))]",
      },
    },
    defaultVariants: {
      side: 'right',
    },
  },
);

interface SheetContentProps
  extends
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {
  /** See `DialogContent.hideCloseButton` — same reason, same single consumer. */
  hideCloseButton?: boolean;
}

const SheetContent: React.FC<SheetContentProps> = ({
  side = 'right',
  className,
  children,
  style,
  hideCloseButton = false,
  ...props
}) => {
  // Reserve iOS safe-area space (notch / Dynamic Island / home indicator /
  // iPhone Duo system bar column), from Capacitor's --safe-area-inset-*
  // variables with env() as the fallback. Resolves to 0px on web, so this is
  // a no-op outside Capacitor. Inline styles win over consumer Tailwind
  // classes like `p-0` so every existing drawer respects insets without
  // per-screen edits; side sheets take their edge inset from the variant.
  const inset = {
    top: 'var(--safe-area-inset-top, env(safe-area-inset-top, 0px))',
    right: 'var(--safe-area-inset-right, env(safe-area-inset-right, 0px))',
    bottom: 'var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px))',
    left: 'var(--safe-area-inset-left, env(safe-area-inset-left, 0px))',
  };
  const safeAreaStyle: React.CSSProperties =
    side === 'top'
      ? { paddingTop: inset.top, left: inset.left, right: inset.right }
      : side === 'bottom'
        ? { paddingBottom: inset.bottom, left: inset.left, right: inset.right }
        : { paddingTop: inset.top, paddingBottom: inset.bottom };

  return (
    <SheetPortal>
      <SheetOverlay />
      <SheetPrimitive.Content
        className={cn(sheetVariants({ side }), className)}
        style={{ ...safeAreaStyle, ...style }}
        {...props}
      >
        {hideCloseButton ? null : (
          <SheetPrimitive.Close
            className="ring-offset-background focus:ring-ring data-[state=open]:bg-secondary absolute end-4 rounded-xs opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none"
            style={{ top: `calc(${inset.top} + 1rem)` }}
          >
            <Cross2Icon className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
        )}
        {children}
      </SheetPrimitive.Content>
    </SheetPortal>
  );
};
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col gap-y-3 text-center sm:text-start", className)}
    {...props}
  />
);
SheetHeader.displayName = 'SheetHeader';

const SheetFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 rtl:sm:space-x-reverse",
      className,
    )}
    {...props}
  />
);
SheetFooter.displayName = 'SheetFooter';

const SheetTitle: React.FC<
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
> = ({ className, ...props }) => (
  <SheetPrimitive.Title
    className={cn("text-foreground text-lg font-semibold", className)}
    {...props}
  />
);
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription: React.FC<
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
> = ({ className, ...props }) => (
  <SheetPrimitive.Description
    className={cn("text-muted-foreground text-sm", className)}
    {...props}
  />
);
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
